var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ArraySchema, MapSchema, Schema, type, view } from "@colyseus/schema";
import { ResourceBag } from "./ResourceBag.js";
import { BuildingConfigState, VigorZoneState, } from "./PlayerConfigState.js";
import { FortressCardState } from "./FortressCardState.js";
import { QueuedMove } from "./QueuedMove.js";
export class Player extends Schema {
    constructor() {
        super(...arguments);
        /** The seat key — the account id of the player who owns it (a guest's
         * synthetic `guest:<session>` id under AUTH_DISABLED). Every other
         * reference to a seat (`ownerId`, `currentPlayerId`, history, …) is this
         * string. A session id is NOT a seat key: connections come and go, the
         * seat stays put, and the server maps sessions to seats at the edge. */
        this.id = "";
        /**
         * The account that owns the seat, or "" for a seat whose account was
         * never recorded (a pre-v26 save that could not be recovered). Equals `id`
         * whenever it is set. Server-side only — NOT decorated, so it never goes
         * over the wire; it exists so the persisted `userId` is an explicit fact
         * about the seat rather than a guess from the key's shape.
         */
        this.userId = "";
        this.name = "";
        this.seat = 0;
        this.connected = true;
        /** Lobby ready flag. The match initializes only once every player is ready. */
        this.ready = false;
        /**
         * This player's standing in the lobby's skill rating pool, populated by the
         * server while the match is ranked with a pool selected and cleared when it
         * isn't. The rating API key lives on the server, so these arrive through
         * state sync rather than a per-client fetch.
         *
         * `ratingLoaded` is the only honest "do we know anything" flag: a player
         * Inhouse has never rated is shown at the pool's starting rating with an
         * empty record, which is indistinguishable from a real 0-match rating —
         * deliberately, since both mean "no matches yet".
         */
        this.ratingLoaded = false;
        /** Current rating, or the pool's starting rating when unrated. */
        this.rating = 0;
        this.ratingWins = 0;
        this.ratingLosses = 0;
        /** Ranked matches played in this pool. 0 renders as "No matches". */
        this.ratingMatches = 0;
        /** Live streak: positive = consecutive wins, negative = losses, 0 = neither. */
        this.ratingStreak = 0;
        /** Graph node id of this player's home base, or -1 before it's placed during
         * the `setup` phase. The node id is the persisted global board coordinate
         * (topology is deterministic from BOARD_RADIUS). */
        this.homeNodeId = -1;
        /** True once this player has assigned their starting Production Track during
         * the `production` setup phase. */
        this.productionSet = false;
        /** Resources on hand. */
        this.resources = new ResourceBag();
        /** Production Track allocation — collected each round's Production phase. */
        this.production = new ResourceBag();
        /**
         * Sparse building-config overrides, keyed by building type. Only entries that
         * differ from the match `gameConfig.buildings` are stored; missing keys fall
         * back to the match snapshot (see `resolveBuilding`).
         */
        this.buildings = new MapSchema();
        /**
         * Sparse vigor-zone overrides, keyed by altitude level ("0".."4"). Only
         * entries that differ from the match `gameConfig.vigorZones` are stored;
         * missing keys fall back to the match snapshot (see `resolveVigorZone`).
         */
        this.vigorZones = new MapSchema();
        /**
         * This player's implemented (face-up) Fortress cards. Each tracks its token
         * count and whether it's been exhausted (disabled). This list is the source
         * of truth for the player's card effects: granted special actions and
         * passive rule changes are DERIVED from it via the effect registry
         * (`effects/registry.ts`), never stored.
         */
        this.fortressCards = new ArraySchema();
        /** Fortresses built, by deck — apex count drives victory. */
        this.baseFortresses = 0;
        this.apexFortresses = 0;
        /** True once the player has passed for the current round. */
        this.passed = false;
        /** Banked "gain 2 resources of any kind" awards (iCarlie's build-nearby
         * trigger), cashed via the claim_resources action. */
        this.pendingResourceClaims = 0;
        /** Banked single "+1 of any resource" picks (Resource Sequencer's
         * lowest-minerals round-start income; Frienemy's neighbour income on games
         * pinned to catalogue "31" and earlier), cashed via the
         * pick_bonus_resources action. */
        this.pendingResourcePicks = 0;
        /** Banked one-time resource units still to distribute on high-zone nodes
         * (Pee-Pee Pinata's implement grant), placed via the
         * drop_one_time_resources action. */
        this.pendingResourceDrops = 0;
        /** Banked market reactions — one per own build on a one-time resource
         * (Miss Mineralverse Maggot's passive), each cashed via the free
         * market_reaction action as a cycle of one card or a lock/unlock. */
        this.pendingMarketActions = 0;
        /** Extra action points dealt on the player's next turn start (Grand
         * Entrance: "+3 actions on the turn that you re-enter"), then cleared. */
        this.pendingBonusActions = 0;
        /**
         * Wall-clock epoch ms when the decision the match is waiting on from this
         * player started (their turn, a setup step, the round-start cycle, a window
         * answer), or 0 when it's waiting on nothing of theirs. With
         * `config.timePerTurnMs` set, the server makes the move for them once
         * `now - turnStartedMs` reaches the limit. The clock never pauses — not
         * while they're disconnected, not while the room is closed (it's
         * persisted). Synced so the client can render a live countdown and a
         * count-up total.
         */
        this.turnStartedMs = 0;
        /**
         * Cumulative ms this player has spent on completed turns this match. Live
         * turn time is `turnTimeMs + (now - turnStartedMs)` while `turnStartedMs > 0`
         * (including while disconnected). Banked on turn end.
         */
        this.turnTimeMs = 0;
        /**
         * Face-down cards in this player's hand. Synced ONLY to the owning client via
         * Colyseus `StateView`: `@view()` gates the field so other players never see
         * it. The room must assign the owner a view at join — e.g.
         * `client.view = new StateView(); client.view.add(player);`.
         */
        this.hiddenCards = new ArraySchema();
        /** Curious Clit's private top-cards reorder prompt. */
        this.pendingReorderCards = new ArraySchema();
        this.pendingReorderMarket = "";
        /** Dungeon Dice Martyr blind choices owed by this player, encoded privately
         * as `victimSessionId|faceDownCountAtTrigger`. */
        this.pendingMartyrDiscards = new ArraySchema();
        /** Timed matches: when the front Martyr choice was put to this player (0
         * when none, or untimed). It gets its own full move-time limit from then —
         * out of time, the card is picked at random. */
        this.martyrPromptStartedMs = 0;
        /** The live Zapped Bapped Statue pay window: the energy penalty the last
         * finalized OPPONENT action paid (0 = no window). The owner may match it
         * onto the Statue until the NEXT action finalizes, which closes the window
         * (and opens that action's own, when it also paid energy). Cleared at round
         * start. Private on the wire — reactive options telegraph the owner's plans. */
        this.pendingVigorOffer = 0;
        /** Auto-pay for QUEUED volleys (banners can't be answered mid-barrage):
         * while enabled, penalties paid by auto-played queued actions are matched
         * automatically as long as the owner keeps at least `vigorAutoPayFloor`
         * energy in the bank afterwards (both private on the wire). */
        this.vigorAutoPay = false;
        this.vigorAutoPayFloor = 0;
        /** BAM Exploder's standing "you may" answer: while enabled (the default),
         * every opponent fortress within this player's attack range of any of their
         * Artifacts of Courage is destroyed automatically at round end (+1 token on
         * the Exploder each). Toggleable any time via
         * `ClientCommand.ArtifactAutoDestroy`; private on the wire like the other
         * standing settings. */
        this.artifactAutoDestroy = true;
        /** Telescopic Arm's standing "you may" answer: while enabled, the
         * holder's successful Barracks attacks CAPTURE the destroyed building
         * instead of razing it, paying the victim one resource
         * (`captureGiveResource`, an index into `[minerals, food, vigor,
         * obsidian]`; a kind the holder doesn't bank falls back to their largest
         * stash). Off by default — capturing skips the plunder a destruction would
         * award. Toggleable any time via `ClientCommand.CaptureSettings`; private
         * on the wire like the other standing settings. */
        this.captureAuto = false;
        this.captureGiveResource = 0;
        /** Standing reserve for Swole Turtle's round-end energy deposit ("pay as
         * many energy resources onto this card as you want"): at every round end the
         * owner's banked energy ABOVE this floor moves onto the card as tokens.
         * 0 (the default) deposits everything. Adjustable any time via
         * `ClientCommand.VigorDepositFloor`; private on the wire like the auto-pay
         * settings. */
        this.vigorDepositFloor = 0;
        /** Pre-planned moves for this player's upcoming turns, auto-played when the
         * matching turn starts (then the turn ends — leftover actions are forfeited).
         * Private on the wire (`@view()`) — only the owner and the server see them. */
        this.queuedMoves = new ArraySchema();
        /** Queued round-start cycle decision, a JSON `RoundStartCycleChoice`
         * (`{"regular":cardIds,"win":cardIds}`; both empty = cycle nothing — the
         * legacy single-id-or-null shape from older saves still decodes) or
         * "" when none is queued. Queueable while this player holds the First-Turn
         * marker; consumed at the next round start ONLY if they actually open that
         * round — dropped unexecuted when the marker moved to someone else. Private
         * on the wire like the queue. */
        this.queuedRoundStartCycle = "";
        /** Fortress card ids this player watches (starred when face-up in a market).
         * Private on the wire (`@view()`) — a watchlist telegraphs strategy, so other
         * players never see it. Seeded at join from the account's `DBUser.watchlist`
         * and written back on every change; guests keep a per-match list only. */
        this.watchedFortresses = new ArraySchema();
    }
}
__decorate([
    type("string")
], Player.prototype, "id", void 0);
__decorate([
    type("string")
], Player.prototype, "name", void 0);
__decorate([
    type("uint8")
], Player.prototype, "seat", void 0);
__decorate([
    type("boolean")
], Player.prototype, "connected", void 0);
__decorate([
    type("boolean")
], Player.prototype, "ready", void 0);
__decorate([
    type("boolean")
], Player.prototype, "ratingLoaded", void 0);
__decorate([
    type("number")
], Player.prototype, "rating", void 0);
__decorate([
    type("uint16")
], Player.prototype, "ratingWins", void 0);
__decorate([
    type("uint16")
], Player.prototype, "ratingLosses", void 0);
__decorate([
    type("uint16")
], Player.prototype, "ratingMatches", void 0);
__decorate([
    type("int16")
], Player.prototype, "ratingStreak", void 0);
__decorate([
    type("int16")
], Player.prototype, "homeNodeId", void 0);
__decorate([
    type("boolean")
], Player.prototype, "productionSet", void 0);
__decorate([
    type(ResourceBag)
], Player.prototype, "resources", void 0);
__decorate([
    type(ResourceBag)
], Player.prototype, "production", void 0);
__decorate([
    type({ map: BuildingConfigState })
], Player.prototype, "buildings", void 0);
__decorate([
    type({ map: VigorZoneState })
], Player.prototype, "vigorZones", void 0);
__decorate([
    type([FortressCardState])
], Player.prototype, "fortressCards", void 0);
__decorate([
    type("uint8")
], Player.prototype, "baseFortresses", void 0);
__decorate([
    type("uint8")
], Player.prototype, "apexFortresses", void 0);
__decorate([
    type("boolean")
], Player.prototype, "passed", void 0);
__decorate([
    type("uint8")
], Player.prototype, "pendingResourceClaims", void 0);
__decorate([
    type("uint8")
], Player.prototype, "pendingResourcePicks", void 0);
__decorate([
    type("uint8")
], Player.prototype, "pendingResourceDrops", void 0);
__decorate([
    type("uint8")
], Player.prototype, "pendingMarketActions", void 0);
__decorate([
    type("uint8")
], Player.prototype, "pendingBonusActions", void 0);
__decorate([
    type("number")
], Player.prototype, "turnStartedMs", void 0);
__decorate([
    type("number")
], Player.prototype, "turnTimeMs", void 0);
__decorate([
    view(),
    type(["string"])
], Player.prototype, "hiddenCards", void 0);
__decorate([
    view(),
    type(["string"])
], Player.prototype, "pendingReorderCards", void 0);
__decorate([
    view(),
    type("string")
], Player.prototype, "pendingReorderMarket", void 0);
__decorate([
    view(),
    type(["string"])
], Player.prototype, "pendingMartyrDiscards", void 0);
__decorate([
    view(),
    type("number")
], Player.prototype, "martyrPromptStartedMs", void 0);
__decorate([
    view(),
    type("uint8")
], Player.prototype, "pendingVigorOffer", void 0);
__decorate([
    view(),
    type("boolean")
], Player.prototype, "vigorAutoPay", void 0);
__decorate([
    view(),
    type("uint8")
], Player.prototype, "vigorAutoPayFloor", void 0);
__decorate([
    view(),
    type("boolean")
], Player.prototype, "artifactAutoDestroy", void 0);
__decorate([
    view(),
    type("boolean")
], Player.prototype, "captureAuto", void 0);
__decorate([
    view(),
    type("uint8")
], Player.prototype, "captureGiveResource", void 0);
__decorate([
    view(),
    type("uint8")
], Player.prototype, "vigorDepositFloor", void 0);
__decorate([
    view(),
    type([QueuedMove])
], Player.prototype, "queuedMoves", void 0);
__decorate([
    view(),
    type("string")
], Player.prototype, "queuedRoundStartCycle", void 0);
__decorate([
    view(),
    type(["string"])
], Player.prototype, "watchedFortresses", void 0);
//# sourceMappingURL=Player.js.map